import { useCallback } from 'react';

import { isAxiosError } from 'axios';
import {
  captureExamEvent,
  captureExamException,
  EXAM_EVENTS,
  EXAM_STEP,
  NETWORK_TRANSPORT,
  secondsSince,
  withNetworkTiming,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { EyePropertyCode } from 'src/models/Eye.model';
import { ExamSubmissionData } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { BackendStatusEnum } from 'src/models/GalenData/DeviceDataView.model';
import { getModels } from 'src/models/Product.model';
import { Session } from 'src/models/Session.model';

import { Exam, OrderIDB, UploadStateEnum } from '../offline/db';
import {
  getNewestCompletedExamIDB,
  getNewestUncompletedExamIDB,
  getNewestUnsyncedExamIDB,
  markExamCreatedInGalenIDB,
  markEyeUploadedIDB,
  setExamUploadStatusIDB,
  setEyeImageIDB,
  setResultPollingStatusIDB,
  updateExamFieldsIDB,
} from '../offline/outboxClient';

// Prevent too many retries if the network is blocked at the router or firewall level
const RETRY_DELAY_MS = 1000;

let isCancelled = false;
let syncPromise: Promise<void> | undefined;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableNetworkError = (error: unknown): boolean =>
  isAxiosError(error) && !error.response;

export default function useIdbSync(resetProgress?: () => void) {
  const {
    session,
    currentUser,
    practiceSettings,
    setBackendStatus,
    updateSession,
  } = useRemoteBoundStore();

  const createEmptyExam = useCallback(
    async (orderId: string, exam: Exam): Promise<void> => {
      const data: ExamSubmissionData = {
        SessionId: exam.ExamId,
        OrderId: orderId,
        ExamSubmissionMetadata: {
          models: getModels(practiceSettings.product),
        },
        IsHidden: false,
        ExamSubmissionDatetime: new Date().toISOString(),
        ReportDatetime: new Date().toISOString(),
        PracticeUserId: currentUser?.userId ?? '',
        PracticeId: currentUser?.currentRole?.practice?.practiceId ?? '',
        SiteId: session.siteId,
        SiteName: session.siteName,
        DiabetesType: exam.TypeOfDiabetes,
        DurationOfDiabetes: exam.LengthOfDiabetesDiagnosis,
        LastEyeExam: exam.TimeSinceLastEyeExam,
        CameraSn: exam.CameraSn,
        CameraVersion: exam.CameraVersion,
      };

      await Api.remote.noRetry.upload_exam(data, session.patientUserId);
    },
    [session, currentUser, practiceSettings],
  );

  const uploadExamData = useCallback(
    async (orderId: string, exam: Exam): Promise<void> => {
      const data: ExamSubmissionData = {
        SessionId: exam.ExamId,
        OrderId: orderId,
        ExamSubmissionMetadata: {
          models: getModels(practiceSettings.product),
        },
        IsHidden: false,
        ExamSubmissionDatetime: new Date().toISOString(),
        ReportDatetime: new Date().toISOString(),
        PracticeUserId: currentUser?.userId ?? '',
        PracticeId: currentUser?.currentRole?.practice?.practiceId ?? '',
        SiteId: session.siteId,
        DiabetesType: exam.TypeOfDiabetes,
        DurationOfDiabetes: exam.LengthOfDiabetesDiagnosis,
        LastEyeExam: exam.TimeSinceLastEyeExam,
        CameraSn: exam.CameraSn,
        CameraVersion: exam.CameraVersion,
        LeftEyeMainCameraGain: exam.LeftEyeMainGain,
        LeftEyeSideCameraGain: exam.LeftEyeSideGain,
        RightEyeMainCameraGain: exam.RightEyeMainGain,
        RightEyeSideCameraGain: exam.RightEyeSideGain,
        SubmittedForAnalysis: exam.SubmittedForAnalysis,
      };

      await Api.remote.noRetry.upload_exam(data, session.patientUserId);
    },
    [session, currentUser, practiceSettings],
  );

  const uploadExamImage = useCallback(
    async (exam: Exam, side: EyePropertyCode): Promise<void> => {
      const blob =
        side === EyePropertyCode.LEFT ? exam.LeftEyeImage : exam.RightEyeImage;
      const fileName =
        side === EyePropertyCode.LEFT
          ? exam.LeftEyeFileName
          : exam.RightEyeFileName;

      if (!blob || !fileName) {
        throw new Error(
          `Missing ${side} image blob/filename for Exam ${exam.ExamId}`,
        );
      }

      const imageUrl = URL.createObjectURL(blob);
      const start = performance.now();
      try {
        const { timingProps } = await withNetworkTiming(
          () =>
            Api.remote.noRetry.upload_eye_image({
              examId: exam.ExamId,
              side,
              imageUrl,
              imageFileName: fileName,
            }),
          { transport: NETWORK_TRANSPORT.INTERNET, payloadBytes: blob.size },
        );
        captureExamEvent(EXAM_EVENTS.IMAGE_UPLOADED, {
          eye_side: side.toLowerCase(),
          ...timingProps,
        });
      } catch (error) {
        captureExamEvent(EXAM_EVENTS.IMAGE_UPLOAD_FAILED, {
          eye_side: side.toLowerCase(),
          duration_seconds: secondsSince(start),
          payload_bytes: blob.size,
        });
        throw error;
      } finally {
        URL.revokeObjectURL(imageUrl);
      }
    },
    [],
  );

  const getNewSession = useCallback(
    async (deviceDataId: string): Promise<Session | undefined> => {
      try {
        const newSession = await Api.remote.load_session({
          deviceDataId,
        });
        updateSession(newSession);
        return newSession;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          void captureExamException(error, {
            step: EXAM_STEP.ANALYSIS,
          }).catch(() => {});
          throw error;
        }
        throw error;
      }
    },
    [updateSession],
  );

  const fetchBackendState = useCallback(
    async (exam: Exam): Promise<void> => {
      try {
        const BackendStatusData = await Api.remote.noRetry.get_backend_state(
          exam.ExamId,
        );
        if (!BackendStatusData) {
          return;
        }

        if (BackendStatusData.GlobalStatus.value === BackendStatusEnum.Failed) {
          setBackendStatus(BackendStatusEnum.Failed);
          return;
        }

        if (
          BackendStatusData.GlobalStatus.value !== BackendStatusEnum.ReportEnd
        ) {
          setBackendStatus(BackendStatusData.GlobalStatus.value);
          await delay(500);
          return;
        }
        const newSession = await getNewSession(exam.ExamId);
        if (!newSession) {
          throw new Error('No new session found');
        }

        if (!newSession.orderId) {
          throw new Error('No orderId found in new session');
        }
        await setResultPollingStatusIDB(
          newSession.orderId,
          exam.ExamId,
          UploadStateEnum.COMPLETED,
        );

        await updateExamFieldsIDB(newSession.orderId, exam.ExamId, {
          LeftEyeResults: newSession.leftResults,
          RightEyeResults: newSession.rightResults,
        });
        setBackendStatus(BackendStatusEnum.ReportEnd);
      } catch (error) {
        void captureExamException(error, {
          step: EXAM_STEP.ANALYSIS,
        }).catch(() => {});
        throw error;
      }
    },
    [getNewSession, setBackendStatus],
  );

  const idbSync = useCallback(
    async (orderId: string): Promise<void> => {
      let newestUnsyncedExam = await getNewestUnsyncedExamIDB(orderId);
      while (!isCancelled && newestUnsyncedExam) {
        const startTime = performance.now();
        try {
          if (!newestUnsyncedExam.ExamCreatedInGalen) {
            await createEmptyExam(orderId, newestUnsyncedExam);
            await markExamCreatedInGalenIDB(orderId, newestUnsyncedExam.ExamId);
          }

          if (
            newestUnsyncedExam.LeftEyeUploadStatus === UploadStateEnum.PENDING
          ) {
            await uploadExamImage(newestUnsyncedExam, EyePropertyCode.LEFT);
            await markEyeUploadedIDB(
              orderId,
              newestUnsyncedExam.ExamId,
              EyePropertyCode.LEFT,
            );
          }

          if (
            newestUnsyncedExam.RightEyeUploadStatus === UploadStateEnum.PENDING
          ) {
            await uploadExamImage(newestUnsyncedExam, EyePropertyCode.RIGHT);
            await markEyeUploadedIDB(
              orderId,
              newestUnsyncedExam.ExamId,
              EyePropertyCode.RIGHT,
            );
          }

          if (
            newestUnsyncedExam.SubmittedForAnalysis &&
            newestUnsyncedExam.ExamUploadStatus === UploadStateEnum.PENDING
          ) {
            await uploadExamData(orderId, newestUnsyncedExam);
            await setExamUploadStatusIDB(
              orderId,
              newestUnsyncedExam.ExamId,
              UploadStateEnum.COMPLETED,
            );
            await setResultPollingStatusIDB(
              orderId,
              newestUnsyncedExam.ExamId,
              UploadStateEnum.PENDING,
            );
          }
        } catch (error) {
          // Still throw errors that aren't network errors
          if (!isRetryableNetworkError(error)) {
            throw error;
          }
          const duration = performance.now() - startTime;
          if (duration < RETRY_DELAY_MS) {
            await delay(RETRY_DELAY_MS);
          }
        } finally {
          newestUnsyncedExam = await getNewestUnsyncedExamIDB(orderId);
        }
      }

      let newestUncompletedExam = await getNewestUncompletedExamIDB(orderId);
      if (!newestUncompletedExam) {
        // Special check for network issue when the last submit button is pressed
        const newestCompletedExam = await getNewestCompletedExamIDB(orderId);
        if (!newestCompletedExam) {
          return;
        }
        resetProgress?.();
        return;
      }

      // Polling has to be seperate since we need newest first and we don't want to finish before all images are uploaded.
      while (!isCancelled && newestUncompletedExam) {
        const startTime = performance.now();
        try {
          if (
            newestUncompletedExam.ResultPollingStatus ===
            UploadStateEnum.PENDING
          ) {
            await fetchBackendState(newestUncompletedExam);
          }
        } catch (error) {
          if (!isRetryableNetworkError(error)) {
            throw error;
          }
          const duration = performance.now() - startTime;
          if (duration < RETRY_DELAY_MS) {
            await delay(RETRY_DELAY_MS);
          }
        } finally {
          newestUncompletedExam = await getNewestUncompletedExamIDB(orderId);
        }
      }
    },
    [
      createEmptyExam,
      uploadExamImage,
      uploadExamData,
      fetchBackendState,
      resetProgress,
    ],
  );

  const runSync = useCallback(
    (orderId: string): Promise<void> => {
      if (syncPromise) {
        return syncPromise;
      }

      isCancelled = false;
      syncPromise = idbSync(orderId).finally(() => {
        syncPromise = undefined;
      });

      return syncPromise;
    },
    [idbSync],
  );

  const cancelSync = useCallback(() => {
    isCancelled = true;
  }, []);

  const restartSync = useCallback(
    (orderId: string): Promise<void> => {
      return runSync(orderId);
    },
    [runSync],
  );

  const setEyeImage = useCallback(
    async (
      orderId: string,
      examId: string,
      side: EyePropertyCode,
      blob: Blob,
      fileName: string,
    ): Promise<OrderIDB> => {
      const order = await setEyeImageIDB(orderId, examId, side, blob, fileName);
      void runSync(orderId).catch(() => {});
      return order;
    },
    [runSync],
  );

  const updateExamFields = useCallback(
    async (
      orderId: string,
      examId: string,
      patch: Partial<Omit<Exam, 'ExamId' | 'UpdatedAt'>>,
    ): Promise<OrderIDB> => {
      try {
        patch.ExamUploadStatus = UploadStateEnum.PENDING;
        const order = await updateExamFieldsIDB(orderId, examId, patch);
        void runSync(orderId).catch(() => {});
        return order;
      } catch (error) {
        // Best-effort telemetry: swallow its own rejection so a failed
        // capture (e.g. no session orderId on this error path) can't surface
        // as an unhandled rejection. The original error is still rethrown.
        void captureExamException(error, {
          step: EXAM_STEP.IMAGING,
        }).catch(() => {});
        throw error;
      }
    },
    [runSync],
  );

  return {
    cancelSync,
    restartSync,
    setEyeImage,
    updateExamFields,
  };
}
